import React, { useState, useEffect } from 'react';
import { useCurrentSystem } from '../context/SystemContext';
import SystemPageHeader from '../components/SystemPageHeader';
import SystemEditor from '../SystemEditor';
import ConfirmDialog from '../ConfirmDialog';
import UserAvatar from '../components/UserAvatar';
import { firestore } from '../common/firebase';
import {
	collection,
	doc,
	updateDoc,
	deleteDoc,
	getDoc,
	query,
	where,
	onSnapshot,
} from 'firebase/firestore';
import { Plus, Trash2, Mail, UserCheck } from 'lucide-react';

function UsersView() {
	const { currentSystem } = useCurrentSystem();
	const [userDetails, setUserDetails] = useState({});
	const [pendingInvites, setPendingInvites] = useState([]);
	const [inviteOpen, setInviteOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	useEffect(() => {
		if (!currentSystem?.guests?.length) {
			setUserDetails({});
			return;
		}
		let cancelled = false;
		const load = async () => {
			const details = {};
			await Promise.all(
				currentSystem.guests.map(async (uid) => {
					try {
						const snap = await getDoc(doc(firestore, 'users', uid));
						if (snap.exists()) {
							const d = snap.data();
							details[uid] = { first: d.first, last: d.last, email: d.email, uid };
						} else {
							details[uid] = { first: '', last: '', email: uid, uid };
						}
					} catch (e) {
						details[uid] = { first: '', last: '', email: uid, uid };
					}
				})
			);
			if (!cancelled) setUserDetails(details);
		};
		load();
		return () => { cancelled = true; };
	}, [currentSystem?.guests]);

	useEffect(() => {
		if (!currentSystem?.uid) {
			setPendingInvites([]);
			return;
		}
		const q = query(collection(firestore, 'guests'), where('system', '==', currentSystem.uid));
		const unsub = onSnapshot(q, (snap) => {
			const rows = [];
			snap.forEach((d) => {
				const data = d.data();
				if (data.invitee === 'pending') {
					rows.push({ uid: d.id, ...data });
				}
			});
			setPendingInvites(rows);
		});
		return () => unsub();
	}, [currentSystem?.uid]);

	if (!currentSystem) {
		return (
			<div className="p-6 text-center text-gray-500 dark:text-gray-400">
				Select a system from the Systems menu to manage users.
			</div>
		);
	}

	const removeGuest = async (guestUid) => {
		const guests = [...(currentSystem.guests || [])];
		const index = guests.indexOf(guestUid);
		if (index >= 0) guests.splice(index, 1);

		const permissions = { ...(currentSystem.permissions || {}) };
		delete permissions[guestUid];

		await updateDoc(doc(firestore, 'systems', currentSystem.uid), {
			guests,
			permissions,
		});
	};

	const deletePendingInvite = async (inviteId) => {
		await deleteDoc(doc(firestore, 'guests', inviteId));
	};

	const handleDeleteConfirm = async () => {
		if (!deleteTarget) return;
		if (deleteTarget.type === 'guest') {
			await removeGuest(deleteTarget.id);
		} else if (deleteTarget.type === 'invite') {
			await deletePendingInvite(deleteTarget.id);
		}
		setDeleteTarget(null);
	};

	const guestRows = Object.values(userDetails);

	return (
		<div className="p-4 sm:p-6 max-w-7xl mx-auto">
			<SystemPageHeader currentSystem={currentSystem} title="Users" subtitle="Guest access for this system" />

			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
				<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100">
					<UserCheck size={18} className="text-[#880e4f]" /> Guests
				</h2>
				<button
					onClick={() => setInviteOpen(true)}
					className="inline-flex items-center justify-center gap-1.5 text-sm px-4 py-2 bg-[#880e4f] hover:bg-[#6b0b3f] text-white rounded-xl font-medium w-full sm:w-auto"
				>
					<Plus size={16} /> Invite User
				</button>
			</div>

			<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden mb-6 overflow-x-auto">
				<table className="w-full text-sm min-w-[280px]">
					<thead className="bg-gray-50 dark:bg-gray-800 text-left">
						<tr>
							<th className="px-4 py-3 font-medium">User</th>
							<th className="px-4 py-3 font-medium hidden sm:table-cell">Email</th>
							<th className="px-4 py-3 font-medium w-16"></th>
						</tr>
					</thead>
					<tbody>
						{guestRows.length > 0 ? guestRows.map((g) => (
							<tr key={g.uid} className="border-t border-gray-100 dark:border-gray-800">
								<td className="px-4 py-3">
									<div className="flex items-center gap-3">
										<UserAvatar user={g} size={32} />
										<div>
											<div className="font-medium">{g.first || g.last ? `${g.first || ''} ${g.last || ''}`.trim() : 'Guest'}</div>
											<div className="text-xs text-gray-500 sm:hidden">{g.email}</div>
										</div>
									</div>
								</td>
								<td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden sm:table-cell">{g.email}</td>
								<td className="px-4 py-3 text-right">
									<button
										onClick={() => setDeleteTarget({ type: 'guest', id: g.uid, name: g.email || g.uid })}
										className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"
										title="Remove guest"
									>
										<Trash2 size={15} />
									</button>
								</td>
							</tr>
						)) : (
							<tr>
								<td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No guests yet.</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{pendingInvites.length > 0 && (
				<>
					<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100 mb-4">
						<Mail size={18} className="text-[#880e4f]" /> Pending Invites
					</h2>
					<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden overflow-x-auto">
						<table className="w-full text-sm min-w-[280px]">
							<thead className="bg-gray-50 dark:bg-gray-800 text-left">
								<tr>
									<th className="px-4 py-3 font-medium">Name</th>
									<th className="px-4 py-3 font-medium hidden sm:table-cell">Email</th>
									<th className="px-4 py-3 font-medium w-16"></th>
								</tr>
							</thead>
							<tbody>
								{pendingInvites.map((inv) => (
									<tr key={inv.uid} className="border-t border-gray-100 dark:border-gray-800">
										<td className="px-4 py-3">
											<div>{inv.name || '—'}</div>
											<div className="text-xs text-gray-500 sm:hidden">{inv.email}</div>
										</td>
										<td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden sm:table-cell">{inv.email}</td>
										<td className="px-4 py-3 text-right">
											<button
												onClick={() => setDeleteTarget({ type: 'invite', id: inv.uid, name: inv.email })}
												className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"
												title="Cancel invite"
											>
												<Trash2 size={15} />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}

			{inviteOpen && (
				<SystemEditor
					open={inviteOpen}
					system={currentSystem}
					newsystem={false}
					onClose={() => setInviteOpen(false)}
					initialTab={1}
				/>
			)}

			{deleteTarget && (
				<ConfirmDialog
					open={!!deleteTarget}
					title={deleteTarget.type === 'guest' ? 'Remove Guest?' : 'Cancel Invite?'}
					message={`Remove ${deleteTarget.name}? This cannot be undone.`}
					yes_text="Remove"
					no_text="Cancel"
					on_yes={handleDeleteConfirm}
					on_no={() => setDeleteTarget(null)}
				/>
			)}
		</div>
	);
}

export default UsersView;
